function assertIsDefined(val, name) {
  if (
    process.env.REACT_APP_NODE_ENV !== "production" &&
    (val === undefined || val === null)
  ) {
    throw new Error(`Expected '${name}' to be defined, but received ${val}`);
  }
}

/**
 * All text-level semantic elements.
 *
 * @see https://html.spec.whatwg.org/multipage/text-level-semantics.html
 *
 * @type {ContentSchema}
 */
const textContentSchema = {
  strong: {},
  em: {},
  s: {},
  del: {},
  ins: {},
  a: { attributes: ["href", "target", "rel", "id"] },
  code: {},
  abbr: { attributes: ["title"] },
  sub: {},
  sup: {},
  br: {},
  small: {},
  // To do: fix blockquote.
  // cite: {},
  q: { attributes: ["cite"] },
  dfn: { attributes: ["title"] },
  data: { attributes: ["value"] },
  time: { attributes: ["datetime"] },
  var: {},
  samp: {},
  kbd: {},
  i: {},
  b: {},
  u: {},
  mark: {},
  ruby: {},
  rt: {},
  rp: {},
  bdi: { attributes: ["dir"] },
  bdo: { attributes: ["dir"] },
  wbr: {},
  "#text": {},
};

// Recursion is needed.
// Possible: strong > em > strong.
// Impossible: strong > strong.
const excludedElements = ["#text", "br"];
Object.keys(textContentSchema)
  .filter((element) => !excludedElements.includes(element))
  .forEach((tag) => {
    const { [tag]: removedTag, ...restSchema } = textContentSchema;
    textContentSchema[tag].children = restSchema;
  });

/**
 * Embedded content elements.
 *
 * @see https://www.w3.org/TR/2011/WD-html5-20110525/content-models.html#embedded-content-0
 *
 * @type {ContentSchema}
 */
const embeddedContentSchema = {
  audio: {
    attributes: ["src", "preload", "autoplay", "mediagroup", "loop", "muted"],
  },
  canvas: { attributes: ["width", "height"] },
  embed: { attributes: ["src", "type", "width", "height"] },
  img: {
    attributes: ["alt", "src", "srcset", "usemap", "ismap", "width", "height"],
  },
  object: {
    attributes: ["data", "type", "name", "usemap", "form", "width", "height"],
  },
  video: {
    attributes: [
      "src",
      "poster",
      "preload",
      "autoplay",
      "mediagroup",
      "loop",
      "muted",
      "controls",
      "width",
      "height",
    ],
  },
};

/**
 * @param {Node | null | undefined} node
 * @return {node is Element} True if node is an Element node
 */
export function isElement(node) {
  return !!node && node.nodeType === node.ELEMENT_NODE;
}

/**
 * Given a DOM node, removes it from the DOM.
 *
 * @param {Node} node Node to be removed.
 * @return {void}
 */
export function remove(node) {
  assertIsDefined(node.parentNode, "node.parentNode");
  node.parentNode.removeChild(node);
}

export function unwrap(node) {
  const parent = node.parentNode;

  assertIsDefined(parent, "node.parentNode");

  while (node.firstChild) {
    parent?.insertBefore(node.firstChild, node);
  }

  parent.removeChild(node);
}

/**
 * @param {Node} node
 * @return {boolean} Node is text content
 */
export function isTextContent(node) {
  const tag = node.nodeName.toLowerCase();
  return textContentSchema.hasOwnProperty(tag) || tag === "span";
}

/**
 * Recursively checks if an element is empty. An element is not empty if it
 * contains text or contains elements with attributes such as images.
 *
 * @param {Element} element The element to check.
 *
 * @return {boolean} Whether or not the element is empty.
 */
export function isEmpty(element) {
  switch (element.nodeType) {
    case element.TEXT_NODE:
      // We cannot use \s since it includes special spaces which we want
      // to preserve.
      return /^[ \f\n\r\t\v\u00a0]*$/.test(element.nodeValue || "");
    case element.ELEMENT_NODE:
      if (element.hasAttributes()) {
        return false;
      } else if (!element.hasChildNodes()) {
        return true;
      }

      return /** @type {Element[]} */ (Array.from(element.childNodes)).every(
        isEmpty,
      );
    default:
      return true;
  }
}

/**
 * Phrasing content elements.
 *
 * @see https://www.w3.org/TR/2011/WD-html5-20110525/content-models.html#phrasing-content-0
 */
const phrasingContentSchema = {
  ...textContentSchema,
  ...embeddedContentSchema,
};

/**
 * Get schema of possible paths for phrasing content.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/Guide/HTML/Content_categories#Phrasing_content
 *
 * @param {string} [context] Set to "paste" to exclude invisible elements and
 *                           sensitive data.
 *
 * @return {Partial<ContentSchema>} Schema.
 */
export function getPhrasingContentSchema(context) {
  if (context !== "paste") {
    return phrasingContentSchema;
  }

  /**
   * @type {Partial<ContentSchema>}
   */
  const {
    u, // Used to mark misspelling. Shouldn't be pasted.
    abbr, // Invisible.
    data, // Invisible.
    time, // Invisible.
    wbr, // Invisible.
    bdi, // Invisible.
    bdo, // Invisible.
    ...remainingContentSchema
  } = {
    ...phrasingContentSchema,
    // We shouldn't paste potentially sensitive information which is not
    // visible to the user when pasted, so strip the attributes.
    ins: { children: phrasingContentSchema.ins.children },
    del: { children: phrasingContentSchema.del.children },
  };

  return remainingContentSchema;
}

/**
 * Find out whether or not the given node is phrasing content.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/Guide/HTML/Content_categories#Phrasing_content
 *
 * @param {Node} node The node to test.
 *
 * @return {boolean} True if phrasing content, false if not.
 */
export function isPhrasingContent(node) {
  const tag = node.nodeName.toLowerCase();
  return getPhrasingContentSchema().hasOwnProperty(tag) || tag === "span";
}

/**
 * Replaces the given node with a new node with the given tag name.
 *
 * @param {Element} node    The node to replace
 * @param {string}  tagName The new tag name.
 *
 * @return {Element} The new node.
 */
export function replaceTag(node, tagName) {
  const newNode = node.ownerDocument.createElement(tagName);

  while (node.firstChild) {
    newNode.appendChild(node.firstChild);
  }

  assertIsDefined(node.parentNode, "node.parentNode");
  node.parentNode.replaceChild(newNode, node);

  return newNode;
}

function insertAfter(newNode, referenceNode) {
  assertIsDefined(referenceNode.parentNode, "referenceNode.parentNode");
  referenceNode?.parentNode?.insertBefore(newNode, referenceNode.nextSibling);
}

/**
 * Given two DOM nodes, replaces the former with the latter in the DOM.
 *
 * @param {Element} processedNode Node to be removed.
 * @param {Element} newNode       Node to be inserted in its place.
 * @return {void}
 */
export function replace(processedNode, newNode) {
  assertIsDefined(processedNode.parentNode, "processedNode.parentNode");
  insertAfter(newNode, processedNode.parentNode);
  remove(processedNode);
}

const noop = () => {};

/**
 * @typedef SchemaItem
 * @property {string[]}                            [attributes] Attributes.
 * @property {(string | RegExp)[]}                 [classes]    Classnames or RegExp to test against.
 * @property {'*' | { [tag: string]: SchemaItem }} [children]   Child schemas.
 * @property {string[]}                            [require]    Selectors to test required children against. Leave empty or undefined if there are no requirements.
 * @property {boolean}                             allowEmpty   Whether to allow nodes without children.
 * @property {(node: Node) => boolean}             [isMatch]    Function to test whether a node is a match. If left undefined any node will be assumed to match.
 */

/** @typedef {{ [tag: string]: SchemaItem }} Schema */

/**
 * Given a schema, unwraps or removes nodes, attributes and classes on a node
 * list.
 *
 * @param {NodeList} nodeList The nodeList to filter.
 * @param {Document} doc      The document of the nodeList.
 * @param {Schema}   schema   An array of functions that can mutate with the provided node.
 * @param {boolean}  inline   Whether to clean for inline mode.
 */
export function cleanNodeList(nodeList, doc, schema, inline) {
  Array.from(nodeList).forEach(
    (/** @type {Node & { nextElementSibling?: unknown }} */ node) => {
      const tag = node.nodeName.toLowerCase();

      // It's a valid child, if the tag exists in the schema without an isMatch
      // function, or with an isMatch function that matches the node.
      if (
        schema.hasOwnProperty(tag) &&
        (!schema[tag].isMatch || schema[tag].isMatch?.(node))
      ) {
        if (isElement(node)) {
          const {
            attributes = [],
            classes = [],
            children,
            require = [],
            allowEmpty,
          } = schema[tag];

          // If the node is empty and it's supposed to have children,
          // remove the node.
          if (children && !allowEmpty && isEmpty(node)) {
            remove(node);
            return;
          }

          if (node.hasAttributes()) {
            // Strip invalid attributes.
            Array.from(node.attributes).forEach(({ name }) => {
              if (name !== "class" && !attributes.includes(name)) {
                node.removeAttribute(name);
              }
            });

            // Strip invalid classes.
            // In jsdom-jscore, 'node.classList' can be undefined.
            // TODO: Explore patching this in jsdom-jscore.
            if (node.classList && node.classList.length) {
              const mattchers = classes.map((item) => {
                if (typeof item === "string") {
                  return (/** @type {string} */ className) =>
                    className === item;
                } else if (item instanceof RegExp) {
                  return (/** @type {string} */ className) =>
                    item.test(className);
                }

                return noop;
              });

              Array.from(node.classList).forEach((name) => {
                if (!mattchers.some((isMatch) => isMatch(name))) {
                  node.classList.remove(name);
                }
              });

              if (!node.classList.length) {
                node.removeAttribute("class");
              }
            }
          }

          if (node.hasChildNodes()) {
            // Do not filter any content.
            if (children === "*") {
              return;
            }

            // Continue if the node is supposed to have children.
            if (children) {
              // If a parent requires certain children, but it does
              // not have them, drop the parent and continue.
              if (require.length && !node.querySelector(require.join(","))) {
                cleanNodeList(node.childNodes, doc, schema, inline);
                unwrap(node);
                // If the node is at the top, phrasing content, and
                // contains children that are block content, unwrap
                // the node because it is invalid.
              } else if (
                node.parentNode &&
                node.parentNode.nodeName === "BODY" &&
                isPhrasingContent(node)
              ) {
                cleanNodeList(node.childNodes, doc, schema, inline);

                if (
                  Array.from(node.childNodes).some(
                    (child) => !isPhrasingContent(child),
                  )
                ) {
                  unwrap(node);
                }
              } else {
                cleanNodeList(node.childNodes, doc, children, inline);
              }
              // Remove children if the node is not supposed to have any.
            } else {
              while (node.firstChild) {
                remove(node.firstChild);
              }
            }
          }
        }
        // Invalid child. Continue with schema at the same place and unwrap.
      } else {
        cleanNodeList(node.childNodes, doc, schema, inline);

        // For inline mode, insert a line break when unwrapping nodes that
        // are not phrasing content.
        if (inline && !isPhrasingContent(node) && node.nextElementSibling) {
          insertAfter(doc.createElement("br"), node);
        }

        unwrap(node);
      }
    },
  );
}

/**
 * Given a schema, unwraps or removes nodes, attributes and classes on HTML.
 *
 * @param {string}                             HTML   The HTML to clean up.
 * @param {import('./clean-node-list').Schema} schema Schema for the HTML.
 * @param {boolean}                            inline Whether to clean for inline mode.
 *
 * @return {string} The cleaned up HTML.
 */
export function removeInvalidHTML(HTML, schema, inline) {
  const doc = document.implementation.createHTMLDocument("");

  doc.body.innerHTML = HTML;

  cleanNodeList(doc.body.childNodes, doc, schema, inline);

  return doc.body.innerHTML;
}
